import { DEFAULT_BREAK, MAX_BREAK, type Drill, type LibraryDrill, type Plan, type PlanBlock, type PlanItem } from '@hoopify/shared';

/** Pure edits on a plan. Each returns a new plan; the builder just keeps the latest. */

/** UUID v4. crypto.randomUUID only exists on https or localhost, so build one from random bytes for plain-http LAN use. */
export function newId(): string {
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function itemFromDrill(d: Drill): PlanItem {
  return {
    id: newId(),
    sourceDrillId: d.id,
    title: d.title,
    minutes: d.minutes,
    group: null,
    description: d.description,
    coachingPoints: d.coachingPoints,
    equipment: d.equipment,
    notes: null,
    minPlayers: d.minPlayers,
    courts: d.courts,
    tags: d.tags,
    diagram: d.diagram,
  };
}

/** A Hoopify library drill as a plan item (not linked to a drill of yours). */
export function itemFromLibrary(d: LibraryDrill): PlanItem {
  return {
    id: newId(),
    sourceDrillId: null,
    title: d.title,
    minutes: d.minutes,
    group: null,
    description: d.variations ? `${d.description}\n\nVariations: ${d.variations}` : d.description,
    coachingPoints: d.coachingPoints,
    equipment: d.equipment,
    notes: null,
    minPlayers: d.minPlayers,
    courts: d.courts,
    tags: d.tags,
    diagram: d.diagram,
  };
}

export function quickItem(title: string, minutes: number): PlanItem {
  return { id: newId(), sourceDrillId: null, title, minutes, group: null, description: null, coachingPoints: null, equipment: null, notes: null, minPlayers: null, courts: null, tags: [], diagram: null };
}

const mapBlocks = (p: Plan, fn: (b: PlanBlock) => PlanBlock | null): Plan => ({
  sections: p.sections.map((s) => ({ ...s, blocks: s.blocks.map(fn).filter((b): b is PlanBlock => !!b) })),
});

/** New time slot in a section, at `index` (end if left out). */
export function addBlock(p: Plan, sectionId: string, item: PlanItem, index?: number): Plan {
  return {
    sections: p.sections.map((s) => {
      if (s.id !== sectionId) return s;
      const blocks = [...s.blocks];
      blocks.splice(index ?? blocks.length, 0, { id: newId(), items: [item] });
      return { ...s, blocks };
    }),
  };
}

/** Another drill in the same time slot, run at the same time. Gives both a group name if they don't have one. */
export function addAlongside(p: Plan, blockId: string, item: PlanItem): Plan {
  return mapBlocks(p, (b) => {
    if (b.id !== blockId || b.items.length >= 6) return b;
    const items = [...b.items, item].map((i, n) => (i.group ? i : { ...i, group: `Group ${n + 1}` }));
    return { ...b, items };
  });
}

export function updateItem(p: Plan, itemId: string, changes: Partial<PlanItem>): Plan {
  return mapBlocks(p, (b) => (b.items.some((i) => i.id === itemId) ? { ...b, items: b.items.map((i) => (i.id === itemId ? { ...i, ...changes } : i)) } : b));
}

/** Removes a drill; an empty time slot goes too. A lone drill left in a slot loses its group name. */
export function removeItem(p: Plan, itemId: string): Plan {
  return mapBlocks(p, (b) => {
    if (!b.items.some((i) => i.id === itemId)) return b;
    const items = b.items.filter((i) => i.id !== itemId);
    if (!items.length) return null;
    return { ...b, items: items.length === 1 && /^Group \d+$/.test(items[0]!.group ?? '') ? [{ ...items[0]!, group: null }] : items };
  });
}

export function removeBlock(p: Plan, blockId: string): Plan {
  return mapBlocks(p, (b) => (b.id === blockId ? null : b));
}

/** Moves a time slot to `index` within `sectionId` (which can be a different section). */
export function moveBlock(p: Plan, blockId: string, sectionId: string, index: number): Plan {
  let moving: PlanBlock | undefined;
  let fromSection = '';
  let fromIndex = -1;
  for (const s of p.sections) {
    const i = s.blocks.findIndex((b) => b.id === blockId);
    if (i >= 0) {
      moving = s.blocks[i];
      fromSection = s.id;
      fromIndex = i;
    }
  }
  if (!moving) return p;
  // Dropping a slot onto its own position (or just after itself) changes nothing.
  const adjusted = fromSection === sectionId && fromIndex < index ? index - 1 : index;
  const without = removeBlock(p, blockId);
  return {
    sections: without.sections.map((s) => {
      if (s.id !== sectionId) return s;
      const blocks = [...s.blocks];
      blocks.splice(Math.max(0, Math.min(adjusted, blocks.length)), 0, moving!);
      return { ...s, blocks };
    }),
  };
}

/** One step up or down, crossing into the neighbouring part when at an edge. */
export function nudgeBlock(p: Plan, blockId: string, dir: -1 | 1): Plan {
  const si = p.sections.findIndex((s) => s.blocks.some((b) => b.id === blockId));
  if (si < 0) return p;
  const section = p.sections[si]!;
  const bi = section.blocks.findIndex((b) => b.id === blockId);
  const target = bi + dir;
  if (target >= 0 && target < section.blocks.length) return moveBlock(p, blockId, section.id, dir === 1 ? target + 1 : target);
  const next = p.sections[si + dir];
  if (!next) return p;
  return moveBlock(p, blockId, next.id, dir === 1 ? 0 : next.blocks.length);
}

/** Moves a drill out of a shared time slot into its own slot just after. */
export function splitItem(p: Plan, itemId: string): Plan {
  for (const s of p.sections) {
    const bi = s.blocks.findIndex((b) => b.items.some((i) => i.id === itemId));
    if (bi < 0) continue;
    const item = s.blocks[bi]!.items.find((i) => i.id === itemId)!;
    if (s.blocks[bi]!.items.length < 2) return p;
    const withoutItem = removeItem(p, itemId);
    return addBlock(withoutItem, s.id, { ...item, group: /^Group \d+$/.test(item.group ?? '') ? null : item.group }, bi + 1);
  }
  return p;
}

export function addSection(p: Plan, title: string): Plan {
  return { sections: [...p.sections, { id: newId(), title, blocks: [], breakAfter: DEFAULT_BREAK }] };
}

/** Drink break after one part (0–2 minutes). */
export function setBreak(p: Plan, sectionId: string, minutes: number): Plan {
  const m = Math.max(0, Math.min(MAX_BREAK, Math.round(minutes)));
  return { sections: p.sections.map((s) => (s.id === sectionId ? { ...s, breakAfter: m } : s)) };
}

/** Same drink break between every part. */
export function setAllBreaks(p: Plan, minutes: number): Plan {
  const m = Math.max(0, Math.min(MAX_BREAK, Math.round(minutes)));
  return { sections: p.sections.map((s) => ({ ...s, breakAfter: m })) };
}

export function renameSection(p: Plan, sectionId: string, title: string): Plan {
  return { sections: p.sections.map((s) => (s.id === sectionId ? { ...s, title } : s)) };
}

export function removeSection(p: Plan, sectionId: string): Plan {
  return { sections: p.sections.filter((s) => s.id !== sectionId) };
}

export function moveSection(p: Plan, sectionId: string, dir: -1 | 1): Plan {
  const i = p.sections.findIndex((s) => s.id === sectionId);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= p.sections.length) return p;
  const sections = [...p.sections];
  [sections[i], sections[j]] = [sections[j]!, sections[i]!];
  return { sections };
}

/** Sections with empty names would fail to save; give them a placeholder. */
export function tidy(p: Plan): Plan {
  return { sections: p.sections.map((s, n) => ({ ...s, title: s.title.trim() || `Part ${n + 1}` })) };
}
